import { bookingRefundOffers, bookingRefundOffersPatch, bookingReleaseOffers, bookingReleaseOffersPatch } from "../../../app/api/cancelBooking";
import { BaseAction } from "../../../core/actions/BaseAction";
import { getTranslatedMessage } from "../../../Lib/Utils/i18n";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { isActionRunSuccessfully } from "../../../Lib/Utils/validationUtil";
import { TrainTypeEnum, TripTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IBookedOffer, IBooking, ISelectedPassengerForCancelIds, } from "../../../Orchestrator/Types/Index";
import { IDefaultActionResult } from "../../../Orchestrator/Types/Interface/ActionParams/DefaultActionResult";
import { IBookingRefundOffersResponse, IBookingReleaseOffersResponse, IRefundOffer, IRefundOfferRequest, IReleaseOffer } from "../../../Orchestrator/Types/Types/BookingTypes";
import { fulfillmentIdSet, isRefundableFulfillment } from "../../../Lib/Utils/refundableFulfillments";
import { showNotification } from "../../../Services/notificationService";
import { getBookingAction } from "../../LogicActions/Booking/GetBookingAction";


/**
 * What one direction's release+refund actually did, so `run()` can stop
 * reporting success for a cancellation the backend rejected.
 */
interface IRefundOutcome {
    /** Ids were sent to /release-offers. False = nothing was refundable. */
    sent: boolean;
    /** A release/refund POST or PATCH came back non-200. */
    failed: boolean;
}

export class ConfirmCancelClickAction extends BaseAction<IDefaultActionResult, {
    selectedBookingIdForCancel: string,
    booking: IBooking,
    selectedPassengerForCancel: ISelectedPassengerForCancelIds[],
    purchaserEmail: string,
    selectedPassengerForCancelInBound: ISelectedPassengerForCancelIds[],
    isFrom: string
}> {
    override async run(): Promise<IDefaultActionResult> {
        let result: IDefaultActionResult = {
            data: null,
            message: "",
            status: "fail"
        }

        // Guards the catch below against refreshing a second time. The first
        // refresh has already replaced the selection atoms, so re-running it
        // would intersect the widened state with itself and undo the narrowing.
        let refreshed = false;

        try {
            const { selectedBookingIdForCancel, booking, selectedPassengerForCancel, purchaserEmail, selectedPassengerForCancelInBound, isFrom } = this.params!;

            const outcomes: IRefundOutcome[] = [];

            // OutBound Cancel Passenger Or Trip
            if (selectedPassengerForCancel.length > 0) {
                outcomes.push(await this.refundSelectedPassenger(selectedBookingIdForCancel, booking, selectedPassengerForCancel, TrainTypeEnum.outbound, purchaserEmail))
            }
            // Inbound Cancel Passenger Or Trip
            if (selectedPassengerForCancelInBound.length > 0) {
                outcomes.push(await this.refundSelectedPassenger(selectedBookingIdForCancel, booking, selectedPassengerForCancelInBound, TrainTypeEnum.inbound, purchaserEmail))
            }

            /**
             * A rejected release/refund used to leave `status = "success"`: every
             * non-200 branch simply fell through, the confirmation toast fired,
             * and CancelBookingInitStep routed to a details page rendering a
             * cancelled leg — for a booking still live on the backend.
             *
             * `sent` distinguishes the two ways nothing happens. One leg of a
             * round trip contributing no ids is normal (its passengers were
             * already refunded, and the other leg still cancels), so it is not
             * a failure on its own — but NO leg sending anything means the
             * passenger pressed Cancel and the app called nothing at all.
             */
            const anySent = outcomes.some(outcome => outcome.sent);
            const anyFailed = outcomes.some(outcome => outcome.failed);

            if (anyFailed || !anySent) {
                console.error(
                    "Cancel booking did not complete:",
                    anyFailed ? "release/refund rejected" : "no refundable fulfillments to send"
                );
                /**
                 * ALWAYS toast. `apiRequest` cannot be relied on to have done it:
                 * `ExcludedAPIsNotification` holds `{endpoint:"bookings",
                 * method:"PATCH"}` and `isNotificationExcluded` matches with
                 * `.includes`, so BOTH release-offers and refund-offers PATCH URLs
                 * are suppressed there. A failed PATCH is precisely the
                 * released-but-not-refunded case — the passenger loses the ticket
                 * AND the money — and it would otherwise close the modal, block
                 * the route, and show nothing at all. `!anySent` raises no toast
                 * either, having called nothing.
                 *
                 * A duplicate toast on the two POST paths (same "api-error"
                 * className) is far cheaper than silence on a money-loss path.
                 */
                showNotification(
                    "api_error",
                    getTranslatedMessage("apiErrorMsg"),
                    "",
                    3,
                    "api-error"
                );
                // Only when something was actually sent. `!anySent` changed
                // nothing server-side, so there is no stale snapshot to replace —
                // and refreshing there would reset the passenger's selection for
                // no reason.
                if (anyFailed) { refreshed = true; await this.refreshAfterFailure(); }
                return result;
            }

            if (isFrom === "cancel-shuttle") {
                getBookingAction.setParams({ bookingId: selectedBookingIdForCancel });
                await getBookingAction.execute();
                // Resting the Recoil state
                updateRecoilState("selectedPassengerForCancelAtom", []);
                updateRecoilState("selectedPassengerForCancelInBoundAtom", []);
            }

            showNotification(
                "info",
                getTranslatedMessage("cancelConfirmTitle"),
                getTranslatedMessage("cancelConfirmMessage"),
                3
            );

            result.status = "success"

        } catch (error) {
            result.status = "fail"
            console.error("Error in ConfirmCancelClick action:", error);
            // Same refresh as the checked-failure branch. refundSelectedPassenger
            // can throw, and that path lands here — leaving the stale snapshot
            // this exists to replace. Skipped when the branch above already ran
            // it, or a throw from inside the refresh itself would re-enter and
            // intersect the widened selection with itself.
            if (!refreshed) await this.refreshAfterFailure();
        }
        finally {
            return result;
        }
    }

    /**
     * Re-read the booking after a failed cancel, so the retry works from live
     * data instead of this snapshot. After a PARTIAL failure (outbound refunded,
     * inbound rejected) the snapshot still calls the outbound admissions
     * FULFILLED, so a retry re-sends ids the backend has just marked REFUNDED —
     * the dead-id batch isRefundableFulfillment exists to keep out — and every
     * retry is rejected too, permanently, until a page reload.
     *
     * MIRRORS CancelBookingRedirectClickAction's `getBookingParams`. Refetching
     * with `{bookingId}` alone is NOT equivalent: GetBookingAction then re-derives
     * from the whole booking and rewrites selectedBookingTripStatusAtom,
     * selectedPassengerForCancelAtom and selectedPassengerForCancelInBoundAtom
     * with every FULFILLED passenger on both legs. CancelTripRefundTicket builds
     * the retry payload straight off those atoms with inboundCheckboxChecked
     * defaulting to true, so a scoped entry (the CDT split-card shape carrying
     * `tripIds`/`passengerData`) would silently re-scope to the FULL booking and
     * the next click would cancel legs and passengers the user never selected.
     * Widening a cancellation is worse than the stale ids this fixes.
     *
     * `selectedTripsDataAtom` is written on entry by CancelBookingRedirectClick
     * and nothing on this page clears it.
     */
    private refreshAfterFailure = async () => {
        try {
            await this.refreshAfterFailureUnsafe();
        } catch (error) {
            // Never throw into the caller: run()'s catch is one of them, and a
            // throw from here would both mask the original failure and re-enter
            // this refresh. A stale retry is worse than a lost refresh, so log it.
            console.error("Cancel booking: refresh after failure threw; retry will use stale data", error);
        }
    }

    private refreshAfterFailureUnsafe = async () => {
        const bookingId = this.params?.selectedBookingIdForCancel;
        if (!bookingId) return;
        const selectedTripsData = getRecoilStateValue<any>("selectedTripsDataAtom");

        // The user's per-passenger checkboxes live in these two atoms
        // (PassengerOutboundCheckAction / MainOutBoundCheckAction write them),
        // and GetBookingAction rewrites both unconditionally with EVERY FULFILLED
        // passenger (:269, :286). Mirroring the trip params is not enough to stop
        // that — the trip dimension and the passenger dimension widen separately.
        const chosenOutbound = getRecoilStateValue<string[]>("selectedPassengerForCancelAtom") || [];
        const chosenInbound = getRecoilStateValue<string[]>("selectedPassengerForCancelInBoundAtom") || [];

        getBookingAction.setParams({
            bookingId,
            direction: selectedTripsData?.direction,
            displayPassengerData: selectedTripsData?.passengers,
            selectedTripsData
        });
        await getBookingAction.execute();

        // Re-narrow to what the user actually picked, INTERSECTED with what is
        // still FULFILLED — so ids the backend has just refunded drop out while
        // passengers they deliberately unchecked do not come back. Without this,
        // unchecking 2 of 3 passengers and hitting a rejected release would
        // silently re-tick all 3, and the next Confirm cancels all 3.
        const asIds = (value: any): string[] =>
            (value || []).map((entry: any) => (typeof entry === "string" ? entry : entry?.passengerId));
        const freshOutbound = asIds(getRecoilStateValue<any[]>("selectedPassengerForCancelAtom"));
        const freshInbound = asIds(getRecoilStateValue<any[]>("selectedPassengerForCancelInBoundAtom"));
        updateRecoilState(
            "selectedPassengerForCancelAtom",
            freshOutbound.filter(id => asIds(chosenOutbound).includes(id))
        );
        updateRecoilState(
            "selectedPassengerForCancelInBoundAtom",
            freshInbound.filter(id => asIds(chosenInbound).includes(id))
        );

        // A failed refresh leaves the stale snapshot in place, so the retry is
        // still doomed — say so rather than letting it look recovered.
        if (!isActionRunSuccessfully(getBookingAction)) {
            console.error("Cancel booking: refresh after failure did not complete; retry will use stale data");
        }
    }

    getAllOfferWithoutExchange(bookedOffers: IBookedOffer[]) {
        const allBookedOfferWithoutExchange: IBookedOffer[] = []
        bookedOffers?.forEach((offer: IBookedOffer) => {
            const offerAdmissions = offer.admissions;
            const isAnyFulfilled = offerAdmissions.find(d => d.status === "EXCHANGED" || d.status === "EXCHANGE_ONGOING")
            if (!isAnyFulfilled) {
                allBookedOfferWithoutExchange.push(offer)
            }
        })
        return allBookedOfferWithoutExchange
    }

    refundSelectedPassenger = async (
        bookingId: string,
        booking: IBooking,
        selectedPassengerForCancel: ISelectedPassengerForCancelIds[],
        tripStatus: string,
        purchaserEmail: string): Promise<IRefundOutcome> => {
        const outcome: IRefundOutcome = { sent: false, failed: false };
        const allBookedOfferWithoutExchange: IBookedOffer[] = this.getAllOfferWithoutExchange(booking.bookedOffers);

        const tripDirection = tripStatus === TrainTypeEnum.outbound ? TripTypeEnum.OUT_BOUND : TripTypeEnum.IN_BOUND;

        const matchingTrips = booking.trips?.filter((trip: any) => trip.direction === tripDirection) || [];
        const matchingTripIds = new Set(matchingTrips.map((trip: any) => trip.id));

        const matchingOffers: IBookedOffer[] = allBookedOfferWithoutExchange.filter((offer: IBookedOffer) => {
            const coveredTripId = offer.tripCoverage?.coveredTripId;
            return coveredTripId && matchingTripIds.has(coveredTripId);
        });

        if (matchingOffers.length > 0) {
            const fulfillmentIds = fulfillmentIdSet(booking);
            const passengerFulfillmentIds: string[] = []

            selectedPassengerForCancel.forEach((passengerId: any) => {
                matchingOffers.forEach((offer) => {
                    const admissions = offer.admissions || [];
                    const passengerAdmissions = admissions.filter((d: any) => d.passengerIds.includes(passengerId));

                    passengerAdmissions.forEach((passengerAdmission) => {
                        // FULFILLED only. This used to accept any admission whose
                        // id appeared in `booking.fulfillments`, and refunded
                        // entries are re-statused there rather than removed — so a
                        // shuttle already cancelled by a change-date went back into
                        // the batch, the backend rejected the whole request, and
                        // the booking was never cancelled. See
                        // isRefundableFulfillment.
                        if (!isRefundableFulfillment(passengerAdmission, fulfillmentIds)) return;
                        if (!passengerFulfillmentIds.includes(passengerAdmission.id)) {
                            passengerFulfillmentIds.push(passengerAdmission.id);
                        }
                    });
                });
            });

            if (passengerFulfillmentIds.length > 0) {
                outcome.sent = true;
                const response: any = await bookingReleaseOffers({ fulfillmentIds: passengerFulfillmentIds }, bookingId);
                if (response.code === 200) {
                    const BookingReleaseOffersResponse: IBookingReleaseOffersResponse = response.answer;
                    const allReleasedId = (BookingReleaseOffersResponse?.releaseOffers || []).map((d: IReleaseOffer) => d.id)

                    // A 200 that released nothing would otherwise skip the loop
                    // with failed=false and refund ids nothing has released. The
                    // refund rejects, so it is recoverable — but it costs a money
                    // call and reads as a refund bug rather than a release one.
                    if (allReleasedId.length === 0) {
                        outcome.failed = true;
                        return outcome;
                    }

                    // Add delay between each release offer patch
                    for (const releaseId of allReleasedId) {
                        const releasePatch: any = await bookingReleaseOffersPatch(releaseId, bookingId);
                        // A failed release PATCH is the worst state this path can
                        // reach — the admission is RELEASED but never refunded, so
                        // the passenger loses the travel right AND the money. It
                        // stops being FULFILLED either way, so isRefundableFulfillment
                        // excludes it and no retry from this screen recovers it.
                        // See shuttleIsReleasedNotRefunded. (Whether a RELEASED entry
                        // stays in `booking.fulfillments` is disputed between that
                        // file and refundableFulfillments.ts — nothing here depends
                        // on the answer, so it is deliberately not asserted.)
                        //
                        // STOP rather than refund on: the refund below is one batch
                        // over every id, so an unreleased id in it gets the whole
                        // request rejected — refunding nothing while costing a
                        // second money call and a muddier diagnosis.
                        // ponytail: leaves the already-released ids released-and-
                        // unrefunded, needing manual recovery. Refunding only the
                        // ids that DID release would need a releaseOfferId ->
                        // fulfillmentId map the response does not carry.
                        if (releasePatch?.code !== 200) {
                            outcome.failed = true;
                            break;
                        }
                        await new Promise(resolve => setTimeout(resolve, 500)); // 500ms delay
                    }
                    if (outcome.failed) return outcome;

                    //Refund Offer
                    const refundOfferRequest: IRefundOfferRequest = {
                        fulfillmentIds: passengerFulfillmentIds,
                        overruleCode: null,
                        refundDate: new Date().toISOString()
                    }

                    await new Promise(resolve => setTimeout(resolve, 500)); // 500ms delay
                    const refundOfferResponse: any = await bookingRefundOffers(refundOfferRequest, bookingId);
                    if (refundOfferResponse.code == 200) {
                        //Refund offer with fulfillment
                        const refundOffer: IBookingRefundOffersResponse = refundOfferResponse.answer
                        const allRefundedId = (refundOffer?.refundOffers || []).map((refund: IRefundOffer) => refund.id)
                        // Symmetric with the releaseOffers guard above: a 200 that
                        // quoted nothing must not read as a completed refund.
                        if (allRefundedId.length === 0) {
                            outcome.failed = true;
                            return outcome;
                        }

                        // Add delay between each refund offer patch
                        for (const refundId of allRefundedId) {
                            const refundPatch: any = await bookingRefundOffersPatch(refundId, bookingId);
                            // No `break` here, unlike the release loop above. Each
                            // refund PATCH is independent — confirming the rest
                            // still returns that money — whereas an unreleased id
                            // poisons the single refund batch. Best-effort, then
                            // report the failure.
                            if (refundPatch?.code !== 200) outcome.failed = true;
                            await new Promise(resolve => setTimeout(resolve, 500)); // 500ms delay
                        }

                        await new Promise(resolve => setTimeout(resolve, 500)); // 500ms delay
                    } else {
                        outcome.failed = true;
                    }
                } else {
                    outcome.failed = true;
                }

            }
        }

        return outcome;
    }

}

export const confirmCancelClickAction = new ConfirmCancelClickAction();